import React, { useEffect, useRef } from 'react';
import Button from '../../forms/Button';
import Input from '../../forms/Input';
import Form from '../forms/FullScreenForm';
import { Warning } from '@material-ui/icons';
import logo from '../../../assets/images/logo.png';

const RECOVERY_CODE_LENGTH = 8;
const TOTP_CODE_LENGTH = 6;
const TOTP_ERROR_ID = 'ckp-2fa-totp-error';
const RECOVERY_ERROR_ID = 'ckp-2fa-recovery-error';

const TwoFactorVerifyForm = props => {
    const totpInputRef = useRef(null);
    const recoveryInputRef = useRef(null);

    const {
        code,
        recoveryCode,
        useRecoveryCode,
        loading,
        errorText,
        onInput,
        onRecoveryCodeInput,
        onSubmit,
        onRecoveryCodeSubmit,
        onToggleRecoveryMode,
        onCancel
    } = props;

    const canSubmitTOTP = !!(code && code.length === TOTP_CODE_LENGTH);
    const canSubmitRecovery = !!(recoveryCode && recoveryCode.length === RECOVERY_CODE_LENGTH);
    const canSubmit = useRecoveryCode ? canSubmitRecovery : canSubmitTOTP;

    useEffect(() => {
        if (!errorText) return;
        const inputInstance = useRecoveryCode ? recoveryInputRef.current : totpInputRef.current;
        const node = inputInstance && inputInstance.inputRef ? inputInstance.inputRef : null;
        node?.focus?.();
    }, [errorText, useRecoveryCode]);

    const handleSubmit = () => {
        if (useRecoveryCode) {
            onRecoveryCodeSubmit();
        } else {
            onSubmit();
        }
    };

    return (
        <Form
            onSubmit={handleSubmit}
            canSubmit={canSubmit && !loading}
            header={<img className="ckp-forms-logo" src={logo} alt="Logo" />}
            body={
                <div className="ckp-2fa-verify">
                    <h2 className="ckp-2fa-title">Two-Factor Authentication</h2>

                    {!useRecoveryCode ? (
                        <>
                            <p className="ckp-2fa-description">Enter the 6-digit code from your authenticator app</p>

                            <div className="ckp-2fa-code-input">
                                <Input
                                    ref={totpInputRef}
                                    label="Authentication Code"
                                    name="code"
                                    type="text"
                                    value={code}
                                    onChange={onInput}
                                    errorMessage={errorText || ''}
                                    helperText={
                                        errorText ? (
                                            <span id={TOTP_ERROR_ID} role="alert" aria-live="assertive">
                                                {errorText}
                                            </span>
                                        ) : (
                                            ''
                                        )
                                    }
                                    inputProps={{
                                        maxLength: TOTP_CODE_LENGTH,
                                        pattern: '[0-9]*',
                                        inputMode: 'numeric',
                                        autoComplete: 'one-time-code',
                                        autoFocus: true,
                                        'aria-invalid': !!errorText,
                                        'aria-describedby': errorText ? TOTP_ERROR_ID : undefined
                                    }}
                                />
                            </div>

                            <div className="ckp-2fa-divider">
                                <span>OR</span>
                            </div>

                            <Button
                                type="button"
                                className="ckp-buttons is-secondary"
                                onClick={onToggleRecoveryMode}
                                label="Use a recovery code"
                            />
                        </>
                    ) : (
                        <>
                            <p className="ckp-2fa-description">Enter one of your 8-character recovery codes</p>

                            <div className="ckp-2fa-code-input ckp-2fa-recovery-input">
                                <Input
                                    ref={recoveryInputRef}
                                    label="Recovery Code"
                                    name="recoveryCode"
                                    type="text"
                                    value={recoveryCode}
                                    onChange={onRecoveryCodeInput}
                                    errorMessage={errorText || ''}
                                    helperText={
                                        errorText ? (
                                            <span id={RECOVERY_ERROR_ID} role="alert" aria-live="assertive">
                                                {errorText}
                                            </span>
                                        ) : (
                                            ''
                                        )
                                    }
                                    inputProps={{
                                        maxLength: RECOVERY_CODE_LENGTH,
                                        autoComplete: 'off',
                                        autoFocus: true,
                                        style: { textTransform: 'uppercase' },
                                        'aria-invalid': !!errorText,
                                        'aria-describedby': errorText ? RECOVERY_ERROR_ID : undefined
                                    }}
                                />
                            </div>

                            <p className="ckp-2fa-support-note">
                                If you've also lost your recovery codes, contact support at:
                                <a href="mailto:techsupport@clearem.tv">techsupport@clearem.tv</a>
                            </p>

                            <div className="ckp-2fa-recovery-warning">
                                <Warning className="ckp-2fa-recovery-warning-icon" />
                                <span>Each recovery code can only be used once</span>
                            </div>
                        </>
                    )}
                </div>
            }
            footer={
                <div className="ckp-2fa-footer">
                    <Button
                        loading={loading}
                        inactive={!canSubmit}
                        label={useRecoveryCode ? 'Login with Recovery Code' : 'Verify'}
                    />
                    <Button type="button" className="ckp-2fa-cancel-link" onClick={onCancel} label="Back to Login" />
                </div>
            }
        />
    );
};

export default TwoFactorVerifyForm;
